import { showModal } from './modalHelper';
import React, { useEffect, useState, useCallback } from 'react';
import apiFetch from './api';

export default function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ 
    username: '', 
    password: '', 
    displayName: '', 
    role: 'user',
    orgao: 'GNR',
    posto: '',
    unidade: '',
    nim: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // reloadUsers é exposto para que handlers (criar/alterar/eliminar) possam recarregar a lista
  const reloadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!currentUser) {
        console.debug('reloadUsers: currentUser não definido ainda, adiando carregamento');
        setLoading(false);
        return;
      }

      const res = await apiFetch('/api/auth/users');
      if (!res.ok) {
        let body = null;
        try { body = await res.text(); } catch (e) { body = '<não foi possível ler corpo>'; }
        console.error('reloadUsers: resposta não OK', { status: res.status, statusText: res.statusText, body });
        throw new Error('Falha ao listar utilizadores');
      }
      const data = await res.json();
      if (data.success && data.users) setUsers(data.users);
      else setUsers([]);
    } catch (err) {
      console.error('Erro reloadUsers', err);
      setError('Erro a carregar utilizadores');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    reloadUsers();
  }, [currentUser, reloadUsers]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm({ username: '', password: '', displayName: '', role: 'user', orgao: 'GNR', posto: '', unidade: '', nim: '' });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.username || !form.password) { 
      setError('username e password são obrigatórios'); 
      return; 
    }
    if (form.nim && form.nim.trim() !== '' && !/^\d{7}$/.test(form.nim.trim())) {
      setError('O NIM deve conter exatamente 7 algarismos numéricos.');
      return;
    }
    try {
      const res = await apiFetch('/api/auth/register', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(form) 
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Utilizador criado com sucesso');
        resetForm();
        await reloadUsers();
      } else {
        setError(data.error || 'Erro ao criar utilizador');
      }
    } catch (err) {
      console.error('Erro register', err);
      setError('Erro ao criar utilizador');
    }
  };

  return (
    <div className="user-management-card">
      <div className="user-management-header">
        <h2>Gestão de Utilizadores</h2>
        <p className="muted">Criar e listar utilizadores do sistema com dados de perfil militar GNR. Apenas administradores têm acesso a esta página.</p>
      </div>

      <div className="user-management-body">
        <section className="user-list">
          <h3>Utilizadores</h3>
          {loading ? (
            <p>Carregando utilizadores...</p>
          ) : (
            <div className="table-responsive">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Utilizador</th>
                    <th>Nome</th>
                    <th>Órgão</th>
                    <th>Posto</th>
                    <th>Unidade</th>
                    <th>NIM</th>
                    <th>Função</th>
                    <th style={{ width: 180 }}>Ações</th>
                    <th>Criado</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 12 }}>Nenhum utilizador encontrado</td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td data-label="Utilizador" className="mono">{u.username}</td>
                      <td data-label="Nome">{u.displayName || u.display_name || '-'}</td>
                      <td data-label="Órgão">{u.orgao || '-'}</td>
                      <td data-label="Posto">{u.posto || '-'}</td>
                      <td data-label="Unidade">{u.unidade || '-'}</td>
                      <td data-label="NIM">{u.nim || '-'}</td>
                      <td data-label="Função"><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                      <td data-label="Ações">
                        {/* Ações visíveis apenas para superadmin */}
                        {((currentUser && (currentUser.username === 'UEPS.CSTE' || currentUser.role === 'superadmin')) || false) ? (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {/* Alterar role rápido */}
                            <select
                              value={u.role}
                              onChange={async (e) => {
                                const novo = e.target.value;
                                if (u.username === 'UEPS.CSTE') { 
                                  await showModal('Ação Não Permitida', 'Não é permitido alterar o perfil de Super Administrador (UEPS.CSTE).', { confirmText: 'OK', type: 'danger' }); 
                                  return; 
                                }
                                try {
                                  const res = await apiFetch(`/api/auth/users/${u.id}/role`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ role: novo }) });
                                  const data = await res.json();
                                  if (data.success) {
                                    await reloadUsers();
                                  } else {
                                    await showModal('Erro', data.error || 'Erro ao alterar perfil do utilizador.', { confirmText: 'OK', type: 'danger' });
                                    await reloadUsers();
                                  }
                                } catch (err) {
                                  console.error('Erro change role', err);
                                  await showModal('Erro', 'Ocorreu um erro de comunicação ao alterar perfil.', { confirmText: 'OK', type: 'danger' });
                                }
                              }}
                            >
                              <option value="user">user</option>
                              <option value="admin">admin</option>
                              <option value="viewer">viewer</option>
                            </select>

                            {/* Eliminar utilizador */}
                            <button
                              className="btn-secondary"
                              onClick={async () => {
                                const confirmed = await showModal('Eliminar Utilizador', `Tem a certeza de que deseja eliminar permanentemente o utilizador "${u.displayName || u.display_name || u.username}"?\n\nEsta ação é irreversível.`, { confirmText: 'Eliminar', cancelText: 'Cancelar', isDanger: true });
                                if (!confirmed) return;
                                if (u.username === 'UEPS.CSTE') { 
                                  await showModal('Ação Não Permitida', 'Não é permitido eliminar a conta de Super Administrador (UEPS.CSTE).', { confirmText: 'OK', type: 'danger' }); 
                                  return; 
                                }
                                try {
                                  const res = await apiFetch(`/api/auth/users/${u.id}`, { method: 'DELETE' });
                                  const data = await res.json();
                                  if (data.success) {
                                    await reloadUsers();
                                  } else {
                                    await showModal('Erro', data.error || 'Erro ao eliminar utilizador', { confirmText: 'OK', type: 'danger' });
                                  }
                                } catch (err) {
                                  console.error('Erro delete user', err);
                                  await showModal('Erro', 'Ocorreu um erro de comunicação ao eliminar utilizador.', { confirmText: 'OK', type: 'danger' });
                                }
                              }}
                            >Eliminar</button>
                          </div>
                        ) : (
                          <span style={{ color: '#666' }}>-</span>
                        )}
                      </td>
                      <td data-label="Criado em">{u.created_at ? new Date(u.created_at).toLocaleString() : (u.createdAt ? new Date(u.createdAt).toLocaleString() : '-')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="user-form">
          <h3 style={{ marginTop: 8 }}>Criar novo utilizador</h3>
          <form onSubmit={handleSubmit} className="form-grid" aria-label="Criar novo utilizador">
            <label className="form-row">
              <span>Utilizador (username) *</span>
              <input name="username" placeholder="ex: gnr.silva" value={form.username} onChange={handleChange} aria-required="true" />
            </label>

            <label className="form-row">
              <span>Nome Completo *</span>
              <input name="displayName" placeholder="ex: António Manuel Silva" value={form.displayName} onChange={handleChange} />
            </label>

            <label className="form-row">
              <span>Palavra-passe *</span>
              <input name="password" placeholder="mínimo 8 caracteres" type="password" value={form.password} onChange={handleChange} aria-required="true" />
            </label>

            <label className="form-row">
              <span>Órgão / Entidade *</span>
              <select name="orgao" value={form.orgao} onChange={handleChange}>
                <option value="GNR">GNR</option>
                <option value="PSP">PSP</option>
                <option value="PJ">PJ</option>
                <option value="Bombeiros">Bombeiros</option>
                <option value="B. Sapadores">B. Sapadores</option>
                <option value="Militar">Militar</option>
                <option value="Proteção Civil">Proteção Civil</option>
                <option value="Policia Municipal">Policia Municipal</option>
                <option value="Popular">Popular</option>
                <option value="Guest">Guest</option>
              </select>
            </label>

            <label className="form-row">
              <span>Posto / Graduação</span>
              <input name="posto" placeholder="ex: Guarda, Cabo, Sargento, Tenente..." value={form.posto} onChange={handleChange} />
            </label>

            <label className="form-row">
              <span>Unidade / Destacamento / Posto Territorial</span>
              <input name="unidade" placeholder="ex: Posto Territorial de Sintra, UEPS..." value={form.unidade} onChange={handleChange} />
            </label>

            <label className="form-row">
              <span>NIM (Nº Identificação Militar - 7 dígitos)</span>
              <input name="nim" maxLength={7} placeholder="ex: 2150123" value={form.nim} onChange={handleChange} />
            </label>

            <label className="form-row">
              <span>Função no Sistema</span>
              <select name="role" value={form.role} onChange={handleChange}>
                <option value="user">User (Operador)</option>
                <option value="admin">Admin (Administrador)</option>
                <option value="viewer">Viewer (Apenas Consulta)</option>
              </select>
            </label>

            <div className="form-actions">
              <button className="btn-primary" type="submit">Criar utilizador</button>
              <button type="button" className="btn-secondary" onClick={resetForm}>Limpar</button>
            </div>

            <div className="form-messages">
              {error && <div className="form-error" role="alert">{error}</div>}
              {success && <div className="form-success" role="status">{success}</div>}
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
